import React, { createContext, useContext, useEffect, useState } from 'react';
import { useUser } from './UserContext';

export type Theme = 'dark' | 'light';

const THEME_STORAGE_KEY = 'aibex-theme';
const DEFAULT_THEME: Theme = 'dark';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const getStoredTheme = (): Theme => {
  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  return storedTheme === 'dark' || storedTheme === 'light'
    ? storedTheme
    : DEFAULT_THEME;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUser();
  const [theme, setTheme] = useState<Theme>(getStoredTheme);

  useEffect(() => {
    if (user?.theme === 'dark' || user?.theme === 'light') {
      setTheme(user.theme);
    }
  }, [user?.theme]);

  useEffect(() => {
    document.documentElement.dataset.aibexTheme = theme;
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
